import React, { useRef, useState, useCallback } from 'react';
import { UploadIcon } from 'lucide-react';

interface FileDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  acceptedTypes?: string[];
  maxFiles?: number;
  label?: string;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFilesSelected,
  disabled = false,
  acceptedTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    'video/mp4', 'video/webm', 'video/quicktime', 'video/avi',
    'application/pdf',
    'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/mpeg'
  ],
  maxFiles = 30,
  label,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const limitedFiles = droppedFiles.slice(0, maxFiles);

    if (limitedFiles.length > 0) {
      onFilesSelected(limitedFiles);
    }
  }, [disabled, maxFiles, onFilesSelected]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const selectedFiles = Array.from(e.target.files || []);
    const limitedFiles = selectedFiles.slice(0, maxFiles);

    if (limitedFiles.length > 0) {
      onFilesSelected(limitedFiles);
    }

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [disabled, maxFiles, onFilesSelected]);

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative border-2 border-dashed  p-8 text-center cursor-pointer transition-all duration-200
        ${disabled
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
          : isDragOver
            ? 'border-indigo-400 bg-indigo-50 scale-[1.02]'
            : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50'
        }
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(', ')}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      <div className="space-y-4">
        {/* Icon */}
        <div className="flex justify-center">
          {isDragOver ? (
            <div className="p-3 bg-indigo-100 rounded-full">
              <UploadIcon className="h-8 w-8 text-indigo-600" />
            </div>
          ) : (
            <div className="p-3 bg-gray-100 rounded-full">
              <UploadIcon className="h-8 w-8 text-gray-400" />
            </div>
          )}
        </div>

        {/* Text */}
        <div className="space-y-2">
          <p className={`text-lg font-medium ${disabled
            ? 'text-gray-400'
            : isDragOver
              ? 'text-indigo-700'
              : 'text-gray-700'
            }`}>
            {isDragOver
              ? 'Drop files here'
              : (label || 'Drag & drop files here')
            }
          </p>
          <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-500'
            }`}>
            Or click to browse your file
          </p>
        </div>
      </div>

      {/* Overlay effect when dragging */}
      {isDragOver && !disabled && (
        <div className="absolute inset-0 bg-indigo-100 bg-opacity-50  pointer-events-none" />
      )}
    </div>
  );
};

export default FileDropzone;
